/**
 * SegmentedControl — the app's pill-style view switch.
 *
 * An inset track with the active item lifted out of it as a white pill. Used where the
 * choice is "which lens am I looking through", not "which value do I submit": a
 * `ToggleButtonGroup`'s default bordered-rectangle look reads as a form input, which is
 * why the raw component is not used directly for a view switch.
 *
 * Geometry is the mock's `.mode` rule, verbatim (`recommandations.html`, 2026-10-07):
 * 4px track padding and gap on `#EEEEF1` at 12px radius; items 36px tall, 16px
 * horizontal padding, 9px radius, 14px/500 in `#55555F`, 8px icon gap. Active item
 * goes white with `#141418` ink at 600 under
 * `0 1px 3px rgba(20,20,24,.12), 0 0 0 1px rgba(20,20,24,.04)` — and its ICON turns
 * violet, which is the only place the brand colour appears in the control.
 *
 * ⚠ `ConversationPersona.tsx`'s local `SEG_SX` is the SAME design, predating this file;
 * it differs only in tinting the active label `primary.dark`. Migrating it is a one-line
 * change but it is a Conversations screen, so it is left for whoever next touches that
 * page — do not add a THIRD copy.
 *
 * Clicking the active segment is a no-op, never a deselect: a view switch has no empty
 * state, and MUI's `exclusive` group hands back `null` for that click.
 */

import type { ReactNode } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export const SEGMENTED_SX: SxProps<Theme> = {
  bgcolor: '#EEEEF1',
  borderRadius: '12px',
  p: '4px',
  gap: '4px',
  alignSelf: 'flex-start',
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '9px !important',
    height: 36,
    px: 2,
    py: 0,
    gap: 1,
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    color: '#55555F',
    lineHeight: 1.2,
    '& .MuiSvgIcon-root': { fontSize: 15 },
    '&:hover': { color: '#141418', bgcolor: 'transparent' },
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: '#fff',
    color: '#141418',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(20,20,24,.12), 0 0 0 1px rgba(20,20,24,.04)',
    // The one spot of brand colour in the control — the label stays ink.
    '& .MuiSvgIcon-root': { color: '#7339E8' },
    '&:hover': { bgcolor: '#fff', color: '#141418' },
  },
};

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Leading mark. Sized by the track (16px), so pass the icon undecorated. */
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Required: the group is a control, and its label is not inferable from the pills. */
  ariaLabel: string;
  sx?: SxProps<Theme>;
}

export default function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  sx,
}: SegmentedControlProps<T>) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      aria-label={ariaLabel}
      onChange={(_e, next: T | null) => {
        if (next) onChange(next);
      }}
      sx={sx ? ([SEGMENTED_SX, sx] as SxProps<Theme>) : SEGMENTED_SX}
    >
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value}>
          {o.icon}
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
